// Local midnight → end-of-day as ISO instants, for "today" range filters
// (e.g. Visit#scheduled_at). Local Date math (setHours/setDate) is DST-safe;
// toISOString() converts to the correct UTC instant for the API.
export function todayRange(): { from: string; to: string } {
  const from = new Date()
  from.setHours(0, 0, 0, 0)

  const to = new Date(from)
  to.setDate(to.getDate() + 1)
  to.setMilliseconds(-1)

  return { from: from.toISOString(), to: to.toISOString() }
}

// Local midnight of `fromDate` → end of `toDate` as ISO instants, for range
// filters over instant columns (e.g. the staging lane's scheduled_at week).
// Same DST-safe local Date math as todayRange, over arbitrary local dates.
export function dayRange(fromDate: string, toDate: string): { from: string; to: string } {
  const from = new Date(`${fromDate}T00:00:00`)

  const to = new Date(`${toDate}T00:00:00`)
  to.setDate(to.getDate() + 1)
  to.setMilliseconds(-1)

  return { from: from.toISOString(), to: to.toISOString() }
}

// An ISO instant's LOCAL calendar date as YYYY-MM-DD — the inverse of dayRange:
// bucketing a scheduled_at under the board's local day columns. Slicing the raw
// string would bucket by UTC and shift evening visits across midnight.
export function localDateOf(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// --- Week math for the planning board (date-only YYYY-MM-DD strings) ---------
// Built on @internationalized/date (already a dep via the calendar/date-picker):
// CalendarDate math is DST-proof for date-only arithmetic, unlike JS Date.

import { getLocalTimeZone, parseDate, today, type CalendarDate } from '@internationalized/date'

// Today as a local date-only YYYY-MM-DD string (for calendar-date filters like
// visits.committed_on — an ISO instant would shift a day across midnight UTC).
export function localToday(): string {
  return today(getLocalTimeZone()).toString()
}

// ISO Monday of the week containing `date` (default: today), as YYYY-MM-DD.
export function weekStart(date?: string): string {
  let d: CalendarDate
  try {
    d = date ? parseDate(date) : today(getLocalTimeZone())
  } catch {
    d = today(getLocalTimeZone()) // a malformed ?week= falls back to this week
  }
  const jsDay = d.toDate(getLocalTimeZone()).getDay() // 0=Sun..6=Sat
  return d.subtract({ days: (jsDay + 6) % 7 }).toString()
}

// The 7 dates (Mon..Sun) of the week starting at `startDate` (a Monday).
export function weekDays(startDate: string): string[] {
  const start = parseDate(startDate)
  return Array.from({ length: 7 }, (_, i) => start.add({ days: i }).toString())
}

export function addWeeks(date: string, weeks: number): string {
  return parseDate(date).add({ weeks }).toString()
}

export function addDays(date: string, days: number): string {
  return parseDate(date).add({ days }).toString()
}

// Signed day count from `a` to `b` (positive when b is later).
export function daysBetween(a: string, b: string): number {
  return parseDate(b).compare(parseDate(a))
}

// Whole LOCAL days since a datetime ISO (0 = today) — the « il y a N j » ages.
// Local components on purpose: slicing the UTC string would age a 9pm signing
// by a day in negative offsets.
export function daysSince(iso: string): number {
  const d = new Date(iso)
  const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return daysBetween(local, localToday())
}

// True when a window end (a date-only `YYYY-MM-DD` string) falls on or before
// `days` from today. Includes already-lapsed windows on purpose — "will have
// exceeded its window within N days" subsumes "already has" (the bank's
// priced-waiting count). Null/blank → false.
//
// The date-only string is parsed in LOCAL time on purpose: `new Date('YYYY-MM-DD')`
// parses as UTC midnight and can shift a day in negative offsets.
export function isDueWithin(dueTo: string | null | undefined, days: number): boolean {
  if (!dueTo) return false

  const limit = new Date()
  limit.setHours(0, 0, 0, 0)
  limit.setDate(limit.getDate() + days)

  const [y, m, d] = dueTo.split('-').map(Number)
  return new Date(y, m - 1, d).getTime() <= limit.getTime()
}

// Overdue = the window lapsed before today — exactly "due within −1 days" at
// day granularity (due <= yesterday ⇔ due < today). One parser, one idiom.
export function isOverdue(dueTo: string | null | undefined): boolean {
  return isDueWithin(dueTo, -1)
}
